import { useEffect, useState } from 'preact/hooks';
import { describeError, type Session } from '../api/client';
import { logUnit, tracker, undoUnit } from '../api/habits';
import { monthFrom, monthLabel, monthOf, shiftMonth, type MonthRange } from '../api/month';
import type { WireTracker } from '../api/wire';

import { HabitCell } from '../components/trackers/HabitCell.jsx';
import { ListRow } from '../components/navigation/ListRow.jsx';
import { EmptyState } from '../components/feedback/EmptyState.jsx';
import { Pressable } from '../components/a11y/Pressable';
import { SectionHeading } from '../components/composed/SectionHeading';

/** Habits by day, a month at a time.
 *
 *  The month counterpart to the Weekly Log's seven-column grid, and the third of
 *  U11's three patterns. Thirty-one columns cannot fit any phone, so the day
 *  columns scroll inside their own container with the habit names pinned — the
 *  same shape the weekly grid needed once its fixed cells were found to be
 *  scrolling the whole page sideways.
 *
 *  Names clip to five characters here, per the truncation table. The weekly grid
 *  wraps them instead, which is a deliberate deviation recorded there: seven
 *  columns leave room for two lines, thirty-one do not.
 */
export function HabitsGrid({
  session,
  journalDate,
  onBack,
}: {
  session: Session;
  journalDate: string;
  onBack: () => void;
}) {
  const [range, setRange] = useState<MonthRange>(() => monthOf(journalDate));
  const [data, setData] = useState<WireTracker | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load(at: MonthRange) {
    try {
      setData(await tracker(session, at.from, at.to));
      setError(null);
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  useEffect(() => {
    let cancelled = false;
    setData(null);
    void (async () => {
      const next = await tracker(session, range.from, range.to).catch((caught) => {
        if (!cancelled) setError(describeError(caught));
        return null;
      });
      if (!cancelled && next) setData(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [session, range]);

  const items = data?.items ?? [];
  const countFor = (habitId: string, date: string) =>
    items.find((h) => h.id === habitId)?.days.find((d) => d.journal_date === date)?.count ?? 0;

  /** Days counted against, which is the month — or the month so far, for the
   *  month in progress. Counting a habit against days that have not happened
   *  yet reads as a deficit rather than as progress. */
  const countedDays = range.days.filter((date) => date <= journalDate || range.month < journalDate.slice(0, 7));

  async function tapCell(habitId: string, date: string, goal: number) {
    const cell = `${habitId}:${date}`;
    if (busy) return;
    setBusy(cell);
    const current = countFor(habitId, date);
    try {
      if (current >= goal && goal > 0) await undoUnit(session, habitId, date, current);
      else await logUnit(session, habitId, date);
      // Refetch rather than patch, so the marks and the hit column can never
      // disagree with the service.
      await load(range);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <main style={page}>
      <header style={header}>
        <div style={eyebrow}>BUJO / HABITS</div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--bujo-space-3)' }}>
          <h1 style={h1}>{monthLabel(range.month)}</h1>
          {/* Abbreviated: two full month names beside the title measured 231px
              and pushed the page 15px past a 320 viewport. */}
          <nav aria-label="Change month" style={{ display: 'flex', gap: 'var(--bujo-space-3)', flex: 'none', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setRange(monthFrom(shiftMonth(range.month, -1)))}
              aria-label={`Previous month, ${monthLabel(shiftMonth(range.month, -1))}`}
              style={step}
            >
              ◀ {shortMonth(shiftMonth(range.month, -1))}
            </button>
            <button
              type="button"
              onClick={() => setRange(monthFrom(shiftMonth(range.month, 1)))}
              aria-label={`Next month, ${monthLabel(shiftMonth(range.month, 1))}`}
              style={step}
            >
              {shortMonth(shiftMonth(range.month, 1))} ▶
            </button>
          </nav>
        </div>
      </header>

      {error ? <p style={errorLine}>! {error}</p> : null}

      <section>
        <SectionHeading note={`◄ SCROLL 01–${range.days.length} ►`}>HABITS × DAYS</SectionHeading>
        {data === null ? null : items.length === 0 ? (
          <EmptyState line="No habits yet." detail="Add one from the CLI or the TUI." />
        ) : (
          <div style={gridScroller}>
            <div style={{ minWidth: 88 + 30 + range.days.length * 32 }}>
              <div style={row}>
                <span style={nameCell} />
                <span style={{ ...hitCell, color: 'var(--bujo-text-meta)' }}>HIT</span>
                {range.days.map((date) => (
                  <span
                    key={date}
                    style={{
                      ...cellSlot,
                      textAlign: 'center',
                      font: `${date === journalDate ? 'var(--bujo-fw-bold)' : 'var(--bujo-fw-regular)'} var(--bujo-fs-badge)/26px var(--bujo-font-mono)`,
                      color: date === journalDate ? 'var(--bujo-cal-ink)' : 'var(--bujo-text-meta)',
                      background: date === journalDate ? 'var(--bujo-accent)' : 'transparent',
                    }}
                  >
                    {date.slice(-2)}
                  </span>
                ))}
              </div>
              {items.map((habit) => {
                const goal = habit.goal_per_day || 1;
                const hit = countedDays.filter((date) => {
                  const count = countFor(habit.id, date);
                  return count > 0 && count >= goal;
                }).length;
                return (
                  <div key={habit.id} style={row}>
                    {/* Five characters, per the truncation table. The full name
                        is in GOALS below, so nothing is only ever abbreviated. */}
                    <span style={nameCell}>{habit.name.slice(0, 5)}</span>
                    <span style={hitCell}>
                      {hit}/{countedDays.length}
                    </span>
                    {range.days.map((date) => (
                      <Pressable
                        key={date}
                        label={`${habit.name}, ${date}: ${countFor(habit.id, date)} of ${goal}`}
                        onPress={() => void tapCell(habit.id, date, goal)}
                        style={cellSlot}
                      >
                        <HabitCell
                          value={countFor(habit.id, date)}
                          goal={goal}
                          counted={goal > 1}
                          today={date === journalDate}
                        />
                      </Pressable>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {items.length > 0 ? (
        <section>
          <SectionHeading>GOALS</SectionHeading>
          {items.map((habit) => (
            <ListRow
              key={habit.id}
              label={habit.name}
              meta={habit.goal_per_day > 1 ? `${habit.goal_per_day}× a day` : 'once a day'}
            />
          ))}
          <div style={footnote}>
            Tap a cell to log one unit · tap a met goal to undo one. Counted habits show the
            number, binary ones the glyph <b style={{ color: 'var(--bujo-state-done)' }}>x</b> or{' '}
            <b>·</b> — never colour alone. Names clip to 5 characters in the pinned column.
          </div>
        </section>
      ) : null}

      <button type="button" onClick={onBack} style={back}>
        ◀ BACK TO INDEX
      </button>
    </main>
  );
}

/** `2026-07` → `JUL`. The full name stays in the accessible label. */
function shortMonth(month: string): string {
  return monthLabel(month).slice(0, 3);
}

const page = { padding: 'var(--bujo-gutter)', maxWidth: 1100, margin: '0 auto' };
const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const step = {
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  cursor: 'pointer',
};
const gridScroller = { overflowX: 'auto' as const, overscrollBehaviorX: 'contain' as const };
const row = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  minHeight: 'var(--bujo-row-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const nameCell = {
  width: 56,
  flex: 'none' as const,
  paddingRight: 'var(--bujo-space-2)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-primary)',
  position: 'sticky' as const,
  left: 0,
  background: 'var(--bujo-surface-page)',
  zIndex: 1,
};
const hitCell = {
  width: 32,
  flex: 'none' as const,
  textAlign: 'right' as const,
  paddingRight: 'var(--bujo-space-2)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-label)',
  position: 'sticky' as const,
  left: 56,
  background: 'var(--bujo-surface-page)',
  zIndex: 1,
};
const cellSlot = { width: 30, flex: 'none' as const };
const footnote = {
  paddingTop: 'var(--bujo-space-3)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.7 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const back = {
  marginTop: 'var(--bujo-space-4)',
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-badge)',
  color: 'var(--bujo-accent)',
  cursor: 'pointer',
};
const errorLine = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)',
  color: 'var(--bujo-state-error)',
};
